/**
 * Utility functions for managing departments
 */

// Standard departments
const standardDepartments = [
  'stock',
  'logistics',
  'purchasing',
  'sales',
  'transport',
  'management',
  'it',
  'accounting',
  'reception',
  'coaching',
  'finance',
  'maintenance',
  'nettoyage'
];

/**
 * Get all departments from localStorage
 * @returns {Array} Array of department names
 */
export const getDepartments = () => {
  try {
    // Try to get departments from localStorage
    const storedDepts = localStorage.getItem('stockpro_departments');
    if (storedDepts) {
      return JSON.parse(storedDepts);
    }
    
    // If no departments in localStorage, initialize with standard departments
    localStorage.setItem('stockpro_departments', JSON.stringify(standardDepartments));
    return standardDepartments;
  } catch (error) {
    console.error('Error getting departments:', error);
    return standardDepartments;
  }
};

/**
 * Add a new department
 * @param {string} departmentName - The name of the department to add
 * @returns {boolean} Success status
 */
export const addDepartment = (departmentName) => {
  try {
    if (!departmentName || typeof departmentName !== 'string') {
      console.error('Department name must be a non-empty string');
      return false;
    }
    
    // Get current departments
    const departments = getDepartments();
    
    // Check if department already exists
    if (departments.includes(departmentName)) {
      console.log('Department already exists:', departmentName);
      return false;
    }
    
    // Add new department
    const updatedDepartments = [...departments, departmentName];
    localStorage.setItem('stockpro_departments', JSON.stringify(updatedDepartments));
    
    // Dispatch custom event to notify other components
    const event = new CustomEvent('localStorageChange', { 
      detail: { key: 'stockpro_departments' } 
    });
    window.dispatchEvent(event);
    
    return true;
  } catch (error) {
    console.error('Error adding department:', error);
    return false;
  }
};

/**
 * Update a department name
 * @param {string} oldName - The current name of the department
 * @param {string} newName - The new name for the department
 * @returns {boolean} Success status
 */
export const updateDepartment = (oldName, newName) => {
  try {
    if (!oldName || !newName || typeof oldName !== 'string' || typeof newName !== 'string') {
      console.error('Department names must be non-empty strings');
      return false;
    }
    
    // Get current departments
    const departments = getDepartments();
    
    // Check if old department exists
    if (!departments.includes(oldName)) {
      console.log('Department not found:', oldName);
      return false;
    }
    
    // Check if new name already exists
    if (oldName !== newName && departments.includes(newName)) {
      console.log('New department name already exists:', newName);
      return false;
    }
    
    // Update department
    const updatedDepartments = departments.map(dept => dept === oldName ? newName : dept);
    localStorage.setItem('stockpro_departments', JSON.stringify(updatedDepartments));
    
    // Update employees with this department
    try {
      const storedEmployees = JSON.parse(localStorage.getItem('stockpro_employees') || '[]');
      const updatedEmployees = storedEmployees.map(employee => {
        if (employee.department === oldName) {
          return { ...employee, department: newName };
        }
        return employee;
      });
      
      localStorage.setItem('stockpro_employees', JSON.stringify(updatedEmployees));
    } catch (error) {
      console.error('Error updating employees with new department name:', error);
    }
    
    // Dispatch custom event to notify other components
    const event = new CustomEvent('localStorageChange', { 
      detail: { key: 'stockpro_departments' } 
    });
    window.dispatchEvent(event);
    
    return true;
  } catch (error) {
    console.error('Error updating department:', error);
    return false;
  }
};

/**
 * Delete a department
 * @param {string} departmentName - The name of the department to delete
 * @returns {boolean} Success status
 */
export const deleteDepartment = (departmentName) => {
  try {
    if (!departmentName || typeof departmentName !== 'string') {
      console.error('Department name must be a non-empty string');
      return false;
    }
    
    // Get current departments
    const departments = getDepartments();
    
    // Check if department exists
    if (!departments.includes(departmentName)) {
      console.log('Department not found:', departmentName);
      return false;
    }
    
    // Check if any employees use this department
    try {
      const storedEmployees = JSON.parse(localStorage.getItem('stockpro_employees') || '[]');
      const employeesWithDepartment = storedEmployees.filter(employee => employee.department === departmentName);
      if (employeesWithDepartment.length > 0) {
        console.log('Cannot delete department with employees:', departmentName);
        return false;
      }
    } catch (error) {
      console.error('Error checking employees for department:', error);
      return false;
    }
    
    // Delete department
    const updatedDepartments = departments.filter(dept => dept !== departmentName);
    localStorage.setItem('stockpro_departments', JSON.stringify(updatedDepartments));
    
    // Dispatch custom event to notify other components
    const event = new CustomEvent('localStorageChange', { 
      detail: { key: 'stockpro_departments' } 
    });
    window.dispatchEvent(event);
    
    return true;
  } catch (error) {
    console.error('Error deleting department:', error);
    return false;
  }
};

/**
 * Get department employee count
 * @param {string} departmentName - The name of the department
 * @returns {number} Number of employees in the department
 */
export const getDepartmentEmployeeCount = (departmentName) => {
  try {
    const storedEmployees = JSON.parse(localStorage.getItem('stockpro_employees') || '[]');
    return storedEmployees.filter(employee => employee.department === departmentName).length;
  } catch (error) {
    console.error('Error getting department employee count:', error);
    return 0;
  }
};